import React, { useState } from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { DateTime } from "luxon";

import { P } from "./constants";
import { fmt } from "./helpers";
import { Badge, InsightsChart } from "./ui";
import {
  METRIC_DEFINITIONS,
  SEGMENT_COLORS,
} from "./overviewModel";
import { useOverviewStyles } from "./OverviewTab.styles";
import { PermissionOnly } from "../../../components/AdminOnly";
import { INSIGHTS_BUDGET_PERMISSIONS } from "../../../lib/insightsPermissions";
import { PERMISSIONS } from "../../../lib/authManager";
import TransactionList from "../../Settings/Transactions/TransactionList";

function SectionHeading({ title, aside }) {
  const classes = useOverviewStyles();

  return (
    <Box className={classes.sectionHead}>
      <Typography variant="h6" className={classes.heading}>
        {title}
      </Typography>
      {aside && (
        <Typography variant="caption" className={classes.aside}>
          {aside}
        </Typography>
      )}
    </Box>
  );
}

function Legend({ color, faded = false, children }) {
  const classes = useOverviewStyles();

  return (
    <Typography variant="caption" className={classes.legend}>
      <span
        className={classes.swatch}
        style={{ background: color, opacity: faded ? 0.55 : 1 }}
      />
      {children}
    </Typography>
  );
}

function MetricTile({ label, value, percentChange, increased, invert = false, detail, comparisonStatus, lead = false, badge, pace }) {
  const classes = useOverviewStyles();

  return (
    <Paper
      elevation={0}
      className={`${classes.tile} ${lead ? classes.leadTile : ""}`}
    >
      <Typography variant="caption" className={classes.label} style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        {label}
        {badge && <Badge color="#666666">{badge}</Badge>}
      </Typography>
      <Typography variant="h5" className={classes.value}>
        {value}
      </Typography>
      {pace ? (
        <div className={classes.metricPace}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, pace.percent))}
            classes={{
              root: classes.progressRoot,
              bar: pace.state === "behind" ? classes.progressBarBehind : classes.progressBar,
            }}
          />
          <Typography variant="caption" className={classes.delta}>
            <strong style={{ color: pace.state === "behind" ? "#FF3B30" : pace.state === "ahead" ? "#2E7D32" : "var(--color-text-secondary)" }}>
              {pace.status}
            </strong>
            <span className={classes.faint}>· {pace.percent}% of pace target</span>
          </Typography>
        </div>
      ) : percentChange != null ? (
        <Typography
          variant="caption"
          className={classes.delta}
          style={{ color: (invert ? !increased : increased) ? "#2E7D32" : "#FF3B30" }}
        >
          <span>{increased ? "▲" : "▼"}</span>
          <strong>{Math.abs(percentChange)}%</strong>
          <span className={classes.faint}>vs prior period</span>
        </Typography>
      ) : comparisonStatus ? (
        <Typography variant="caption" className={classes.delta}>
          {comparisonStatus}
        </Typography>
      ) : null}
      {!pace && detail && (
        <Typography variant="caption" className={classes.faint} component="div">
          {detail}
        </Typography>
      )}
    </Paper>
  );
}

function BudgetInputs({ inputs, onSave }) {
  const classes = useOverviewStyles();

  return (
    <Paper elevation={0} className={classes.budgetInputs}>
      <Typography variant="subtitle2" className={classes.heading}>
        Budget Inputs
      </Typography>
      {inputs.map((input) => (
        <Box key={input.key} className={classes.inputRow}>
          <Typography variant="body2" className={classes.muted}>
            {input.label}
          </Typography>
          <Box className={classes.inputControl}>
            {input.prefix && <span>{input.prefix}</span>}
            <TextField
              variant="outlined"
              size="small"
              type="number"
              value={input.value}
              disabled={input.locked}
              className={classes.numInput}
              inputProps={{ min: 0, max: input.max, step: input.step }}
              onChange={(event) => {
                const value = Number(event.target.value) || 0;
                input.setValue(Math.min(input.max, Math.max(0, value)));
              }}
            />
            <Button
              variant="outlined"
              size="small"
              className={classes.smallButton}
              onClick={() => {
                const nextLocked = !input.locked;
                input.setLocked(nextLocked);
                onSave(input.key, input.value, nextLocked);
              }}
            >
              {input.locked ? "Locked" : "Lock"}
            </Button>
          </Box>
        </Box>
      ))}
    </Paper>
  );
}

export function HeadlineSection({ viewModel, budgetInputs, onSaveBudget, includesToday, leakageStatus, previousLeakageStatus, occupancyStatus }) {
  const classes = useOverviewStyles();
  const { data } = viewModel;

  return (
    <section aria-label="Headline metrics">
      <Box className={classes.grid4}>
        <MetricTile
          lead
          label="Hotel net revenue vs budget"
          value={data.metrics.hotelNetRevenue.displayValue}
          percentChange={data.metrics.hotelNetRevenue.percentChange}
          increased={data.metrics.hotelNetRevenue.increased}
          pace={{
            percent: viewModel.pacePercent,
            state: viewModel.paceState,
            status: viewModel.paceStatus,
          }}
        />
        <MetricTile
          label="Revenue leakage"
          badge="Estimated"
          value={data.leakage ? fmt(data.leakage.totalAmount) : leakageStatus}
          percentChange={data.leakage?.percentChange}
          increased={data.leakage?.increased}
          invert
          comparisonStatus={data.leakage ? previousLeakageStatus : null}
          detail={data.leakage ? `${data.leakage.percentOfGrossRevenue}% of gross charges` : null}
        />
        <MetricTile
          label="Parking occupancy"
          badge={includesToday ? "Observed" : null}
          value={data.metrics.parkingOccupancy?.displayValue || occupancyStatus}
          percentChange={data.metrics.parkingOccupancy?.percentChange}
          increased={data.metrics.parkingOccupancy?.increased}
        />
        <MetricTile
          label="DRIVO charge rate"
          value={data.metrics.drivoChargeRate?.displayValue || occupancyStatus}
          percentChange={data.metrics.drivoChargeRate?.percentChange}
          increased={data.metrics.drivoChargeRate?.increased}
        />
      </Box>

      <details className={classes.disclosure}>
        <summary className={classes.disclosureSummary}>About these metrics</summary>
        <Box className={classes.aboutGrid}>
          <div>
            {METRIC_DEFINITIONS.map(({ term, text }) => (
              <div key={term} className={classes.definition}>
                <Typography variant="body2" className={classes.term}>
                  {term}
                </Typography>
                <Typography variant="body2" className={classes.definitionText}>
                  {text}
                </Typography>
              </div>
            ))}
          </div>
          <PermissionOnly permissions={INSIGHTS_BUDGET_PERMISSIONS}>
            <BudgetInputs inputs={budgetInputs} onSave={onSaveBudget} />
          </PermissionOnly>
        </Box>
      </details>
    </section>
  );
}

export function ActionsSection({ viewModel, onNavigate, availableTabs, leakageStatus }) {
  const classes = useOverviewStyles();

  return (
    <section>
      <SectionHeading title="Action required" aside={viewModel.data.currentPeriodLabel} />
      <Paper elevation={0} className={`${classes.surface} ${classes.actionList}`}>
        {!viewModel.data.leakage && <Typography variant="body2">Leakage actions: {leakageStatus}</Typography>}
        {viewModel.actions.map((action, index) => (
          <Box
            key={action.tag}
            className={`${classes.actionRow} ${index === 0 ? classes.actionRowFirst : ""}`}
          >
            <div>
              <Typography variant="caption" className={classes.actionTag}>
                {action.tag}
              </Typography>
              <Typography variant="body2" className={classes.actionTitle}>
                {action.title}
              </Typography>
              {action.detail && (
                <Typography variant="body2" className={classes.actionDetail}>
                  {action.detail}
                </Typography>
              )}
            </div>
            {availableTabs.includes(action.tab) && (
              <Button
                variant="contained"
                disableElevation
                className={classes.primaryButton}
                onClick={() => onNavigate(action.tab)}
              >
                {action.label} →
              </Button>
            )}
          </Box>
        ))}
      </Paper>
    </section>
  );
}

export function formatRevenueInterval(startDate, endDate) {
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate).minus({ days: 1 });
  if (!start.isValid || !end.isValid) return `${startDate} to ${endDate}`;
  if (start.hasSame(end, "day")) return start.toFormat("d LLL yyyy");
  const sameMonth = start.month === end.month && start.year === end.year;
  return sameMonth
    ? `${start.day} to ${end.toFormat("d LLL yyyy")}`
    : `${start.toFormat("d LLL yyyy")} to ${end.toFormat("d LLL yyyy")}`;
}

export function RevenueSection({
  viewModel,
  installation,
  showGateCharges,
  startDate,
  endDate,
  asOf,
}) {
  const classes = useOverviewStyles();
  const [transactionsOpen, setTransactionsOpen] = useState(false);
  const { data, compareLabel } = viewModel;
  const currentRevenueColor = data.metrics.hotelNetRevenue.increased ? "#2E7D32" : "#FF3B30";
  const intervalLabel = formatRevenueInterval(startDate, endDate);

  const chartSeries = [
    {
      key: "previousHotelNetRevenue",
      name: compareLabel,
      color: "#D2D6DA",
      width: 1.5,
      dash: 5,
    },
    {
      key: "hotelNetRevenue",
      name: "Hotel net revenue",
      color: "#241C8A",
      width: 2.5,
    },
    {
      key: "refunds",
      name: "Refunds",
      color: "#FF3B30",
      width: 2,
      dash: 3,
    },
  ];

  return (
    <section className={classes.surface}>
      <SectionHeading
        title="Parking revenue: Hotel net revenue"
        aside={intervalLabel}
      />

      <Box className={classes.revenueOverview}>
        <Box className={classes.revenueSummary}>
          <div className={classes.summaryItem}>
          <Typography variant="caption" className={classes.label}>
            Hotel net revenue
          </Typography>
          <Typography variant="h5" className={classes.summaryValue}>
            {data.metrics.hotelNetRevenue.displayValue}
          </Typography>
          <Typography variant="caption" style={{ color: currentRevenueColor }}>
            {data.metrics.hotelNetRevenue.increased ? "▲" : "▼"} {Math.abs(data.metrics.hotelNetRevenue.percentChange)}% vs {compareLabel}
          </Typography>
          </div>
          <div className={classes.summaryItem}>
          <Typography variant="caption" className={classes.label}>
            Prior period
          </Typography>
          <Typography variant="h6" className={classes.summaryValue}>
            {fmt(viewModel.previousPeriodRevenue)}
          </Typography>
          <Typography variant="caption" className={classes.faint}>
            {compareLabel}
          </Typography>
          </div>
          <div className={classes.summaryItem}>
          <Typography variant="caption" className={classes.label}>
            Refunds
          </Typography>
          <Typography variant="h6" className={classes.summaryValue}>
            {fmt(viewModel.refundTotal)}
          </Typography>
          <Typography variant="caption" className={classes.faint}>
            Successful refunds
          </Typography>
          </div>
          <div>
          <Typography variant="caption" className={classes.label}>
            Hotel net revenue vs budget
          </Typography>
          <Typography variant="h6" className={classes.summaryValue}>
            {viewModel.pacePercent}% of pace
          </Typography>
          <Typography variant="caption" className={classes.faint}>
            {fmt(viewModel.remainingBudget)} remaining
          </Typography>
          </div>
        </Box>
        <Box className={classes.revenueActions}>
          <PermissionOnly permissions={[PERMISSIONS.TRANSACTIONS_READ, PERMISSIONS.INSIGHTS_REVENUE_READ]}>
            <Button
              variant="outlined"
              size="small"
              className={classes.smallButton}
              onClick={() => setTransactionsOpen((open) => !open)}
              aria-expanded={transactionsOpen}
              aria-controls="overview-transactions"
            >
              {transactionsOpen ? "Hide transactions" : "View transactions"}
            </Button>
          </PermissionOnly>
        </Box>
      </Box>

      {transactionsOpen && (
        <PermissionOnly permissions={[PERMISSIONS.TRANSACTIONS_READ, PERMISSIONS.INSIGHTS_REVENUE_READ]}>
          <Box id="overview-transactions" className={classes.revenueTransactions}>
            <TransactionList
              embedded
              installation={installation}
              installationId={installation?.id}
              showGateCharges={showGateCharges}
              startDate={`${startDate} 00:00`}
              endDate={asOf && asOf !== "23:59"
                ? `${DateTime.fromISO(endDate).minus({ days: 1 }).toISODate()} ${asOf}`
                : `${endDate} 00:00`}
              displayZone={installation?.timeZoneName}
            />
          </Box>
        </PermissionOnly>
      )}

      <Box className={classes.legendRow}>
        <Legend color="#241C8A">Hotel net revenue · {data.currentPeriodLabel}</Legend>
        <Legend color="#D2D6DA" faded>{compareLabel}</Legend>
        <Legend color="#FF3B30">Refunds</Legend>
      </Box>
      <Box className={classes.chart}>
        <InsightsChart
          type="line"
          data={viewModel.revenueChart}
          xKey="dateLabel"
          yaxis={{
            formatter: (value) => value >= 1000
              ? `$${(value / 1000).toFixed(1)}k`
              : `$${value}`,
          }}
          series={chartSeries}
        />
      </Box>
    </section>
  );
}

function LeakageCard({ leakageViewModel, onNavigate, status }) {
  const classes = useOverviewStyles();
  const intervalLabel = leakageViewModel
    ? formatRevenueInterval(leakageViewModel.startDate, leakageViewModel.endDate)
    : "";

  return (
    <Paper elevation={0} className={classes.surface}>
      <SectionHeading
        title="Where revenue leaks"
        aside={intervalLabel ? `${intervalLabel} · incident dates` : null}
      />
      {leakageViewModel && <Box className={classes.leakList}>
        {leakageViewModel.rows.map(({
          label,
          amount,
          count,
          averagePerDay,
          limitPerDay,
          progress,
        }) => (
          <div key={label}>
            <Box className={classes.leakRow}>
              <Typography variant="caption">
                {label}
                <span className={classes.faint}> · {count}</span>
              </Typography>
              <Typography variant="caption" className={classes.term}>
                {fmt(amount)}
              </Typography>
            </Box>
            {progress !== null && <>
              <LinearProgress
                variant="determinate"
                value={progress}
                aria-label={`${label}: ${averagePerDay} per day against ${limitPerDay} per day`}
                classes={{ root: classes.progressRoot, bar: classes.progressBar }}
              />
              <Typography variant="caption" className={classes.faint} component="div">
                {averagePerDay}/day of {limitPerDay}/day configured
              </Typography>
            </>}
          </div>
        ))}
      </Box>
      }
      {!leakageViewModel && <Typography variant="body2">{status || "Unavailable"}</Typography>}
      <Typography
        variant="caption"
        className={`${classes.faint} ${classes.topMargin12}`}
        component="div"
      >
        Progress compares each selected period's daily average with its configured
        daily limit. Failed Stripe charges have no configured limit.
      </Typography>
      <Button
        className={`${classes.smallButton} ${classes.topMargin8}`}
        style={{ color: P.nv }}
        disabled={!leakageViewModel}
        onClick={() => onNavigate("leakage")}
      >
        Open leakage →
      </Button>
    </Paper>
  );
}

function SegmentsCard({ rows, status }) {
  const classes = useOverviewStyles();

  return (
    <Paper elevation={0} className={classes.surface}>
      <SectionHeading title="Revenue by segment" aside="Hotel net revenue" />
      <Box className={classes.segmentTableWrap}>
        <table className={classes.segmentTable}>
          <thead>
            <tr>
              <th className={classes.tableHead} style={{ textAlign: "left" }}>Segment</th>
              <th className={classes.tableHead} style={{ textAlign: "right" }}>Hotel net revenue</th>
              <th className={classes.tableHead} style={{ textAlign: "right" }}>Share</th>
            </tr>
          </thead>
          <tbody>
            {status && <tr><td colSpan={3} className={classes.tableCell}>{status}</td></tr>}
            {rows.map((segment, index) => (
                <tr key={segment.id}>
                  <td className={classes.tableCell} style={{ fontWeight: 600 }}>
                    <span
                      className={classes.segmentDot}
                      style={{ background: SEGMENT_COLORS[index] }}
                    />
                    {segment.name}
                  </td>
                  <td className={classes.tableCell} style={{ textAlign: "right", color: P.headline, fontWeight: 600 }}>
                    {fmt(segment.revenue)}
                  </td>
                  <td
                    className={classes.tableCell}
                    style={{
                      textAlign: "right",
                      color: "var(--color-text-secondary)",
                    }}
                  >
                    {segment.share}%
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Typography
        variant="caption"
        className={`${classes.faint} ${classes.topMargin10}`}
        component="div"
      >
        Share is each segment's Hotel net revenue divided by total Hotel net
        revenue for the selected period.
      </Typography>
    </Paper>
  );
}

export function LeakageAndSegmentsSection({
  leakageViewModel,
  segmentRows,
  onNavigate,
  leakageStatus,
  segmentsStatus,
}) {
  const classes = useOverviewStyles();

  return (
    <section className={classes.grid2}>
      <LeakageCard
        leakageViewModel={leakageViewModel}
        onNavigate={onNavigate}
        status={leakageStatus}
      />
      <SegmentsCard rows={segmentRows} status={segmentsStatus} />
    </section>
  );
}

export function OccupancySection({
  occupancyViewModel,
  includesToday,
  requestedStartDate,
  requestedEndDate,
  status,
}) {
  const classes = useOverviewStyles();
  const currentLabel = occupancyViewModel
    ? formatRevenueInterval(occupancyViewModel.startDate, occupancyViewModel.endDate)
    : requestedStartDate && requestedEndDate
      ? formatRevenueInterval(requestedStartDate, requestedEndDate)
      : "";
  const previousLabel = occupancyViewModel
    ? formatRevenueInterval(
        occupancyViewModel.previousStartDate,
        occupancyViewModel.previousEndDate,
      )
    : "prior period";
  const chartSeries = [
    { key: "previousParking", name: `Parking ${previousLabel}`, color: "#D2D6DA", width: 1.5, dash: 5 },
    { key: "previousHotel", name: `Hotel ${previousLabel}`, color: "#D2D6DA", width: 1.5, dash: 3, opacity: 0.65 },
    { key: "parking", name: `Parking ${currentLabel}`, color: "#241C8A", width: 2.5 },
    { key: "hotel", name: `Hotel ${currentLabel}`, color: "#241C8A", width: 2.5, opacity: 0.45 },
  ];
  const parkingDelta = occupancyViewModel?.parkingDelta || 0;
  const hotelDelta = occupancyViewModel?.hotelDelta || 0;

  return (
    <section className={classes.surface}>
      <SectionHeading
        title="Parking vs hotel occupancy"
        aside={currentLabel
          ? `${currentLabel}${occupancyViewModel ? ` · ${occupancyViewModel.granularity} snapshots` : ""}`
          : null}
      />
      {!occupancyViewModel && <Typography variant="body2">{status || "Unavailable"}</Typography>}
      {occupancyViewModel && <>
        <Box className={classes.occupancyStats}>
        <div>
          <Typography variant="caption" className={`${classes.label} ${classes.occupancyLabel}`}>
            Parking occupancy {includesToday && <Badge color="#666666">Observed</Badge>}
          </Typography>
          <Typography variant="h5" className={classes.summaryValue}>
            {occupancyViewModel.parkingOccupancyPercent}%
          </Typography>
          <Typography variant="caption" style={{ color: parkingDelta >= 0 ? "#2E7D32" : "#FF3B30" }}>
            {parkingDelta >= 0 ? "▲" : "▼"} {Math.abs(parkingDelta)}% vs {previousLabel}
          </Typography>
        </div>
        <div>
          <Typography variant="caption" className={`${classes.label} ${classes.occupancyLabel}`}>
            Hotel occupancy <Badge color="#666666">Confirmed</Badge>
          </Typography>
          <Typography variant="h5" className={classes.summaryValue}>
            {occupancyViewModel.hotelOccupancyPercent}%
          </Typography>
          <Typography variant="caption" style={{ color: hotelDelta >= 0 ? "#2E7D32" : "#FF3B30" }}>
            {hotelDelta >= 0 ? "▲" : "▼"} {Math.abs(hotelDelta)}% vs {previousLabel}
          </Typography>
        </div>
        </Box>
      <Box className={classes.legendRow}>
        <Legend color="#241C8A">Parking</Legend>
        <Legend color="#241C8A" faded>Hotel</Legend>
        <Legend color="#D2D6DA" faded>Prior period</Legend>
      </Box>
      <Box className={classes.occupancyChart}>
        <InsightsChart
          type="line"
          data={occupancyViewModel.points}
          xKey="label"
          yaxis={{ max: 100, formatter: (value) => `${value}%` }}
          series={chartSeries}
        />
      </Box>
      </>}
    </section>
  );
}
